import React, { useCallback, useEffect, useState } from "react";
import { FaSpinner, FaExclamationCircle } from "react-icons/fa";
import { dailyReportingApi } from "../dailyReportingApi";
import SectionCard from "./SectionCard";
import ActivityTable from "./ActivityTable";
import TomorrowPlan from "./TomorrowPlan";
import Modal from "./Modal";
import ActivityDetailBody from "./ActivityDetailBody";
import ReportViewTabs from "./ReportViewTabs";
import { ACCENT } from "./reportingStyles";

const formatLogDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function EmployeeReportContent({ employeeId, date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [activeTab, setActiveTab] = useState("today");

  const fetchReport = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dailyReportingApi.getEmployeeReport(employeeId, { date });
      if (res?.data?.success) setData(res.data.data);
      else setError(res?.data?.message || "Failed to load report.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, date]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setActiveTab("today");
    setActiveTask(null);
  }, [employeeId, date]);

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-2 py-14 text-slate-400">
        <FaSpinner className="animate-spin" size={18} style={{ color: ACCENT }} />
        <p className="text-xs">Loading report…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-14 text-rose-500">
        <FaExclamationCircle size={18} />
        <p className="text-xs">{error}</p>
      </div>
    );
  }

  const report = data?.report || {};
  const activities = data?.today_activities || [];
  const tomorrow = data?.tomorrow_plan || [];

  return (
    <div className="space-y-4 p-4 sm:p-5">
      <ReportViewTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        todayCount={activities.length}
        tomorrowCount={tomorrow.length}
      />

      {activeTab === "today" ? (
        <SectionCard
          title="Today's tasks"
          subtitle={`${activities.length} reported · ${formatLogDate(report.date || date)}`}
          emphasis
        >
          <ActivityTable
            activities={activities}
            showNotes
            onRowClick={setActiveTask}
          />
        </SectionCard>
      ) : (
        <SectionCard
          title="Plan for tomorrow"
          subtitle={
            tomorrow.length > 0
              ? `${tomorrow.length} task${tomorrow.length === 1 ? "" : "s"} planned`
              : "No plan submitted yet"
          }
          emphasis
        >
          <TomorrowPlan tasks={tomorrow} onRowClick={setActiveTask} />
        </SectionCard>
      )}

      <Modal
        open={!!activeTask}
        onClose={() => setActiveTask(null)}
        eyebrow="Activity detail"
        title={activeTask?.description || "Task"}
        width="max-w-lg"
        zClass="z-[60]"
        footer={
          <button
            type="button"
            className="daily-report-modal-footer-btn"
            onClick={() => setActiveTask(null)}
          >
            Close
          </button>
        }
      >
        <div className="p-4 sm:p-5">
          <ActivityDetailBody task={activeTask} />
        </div>
      </Modal>
    </div>
  );
}

export default EmployeeReportContent;
